import React from "react";
import AccordionItem from "../common/AccordionItem";
import { Card } from "luna-react/components/card";

export default () => {
  return (
    <Card className="privacy_policy__wrapper">
      <div className="section_header center-text">
        <h1 className="section_title">Sainsbury's Group Privacy Policy</h1>
        <span className="section_subtext">&nbsp;</span>
      </div>
      <p>
        We understand that your privacy and the security of your personal data
        is extremely important. This notice sets out what we do with your
        personal data, how we secure it, where we collect it from and your
        rights in it.
      </p>
      <p>
        This policy applies if you interact with us. This could be in our
        stores, over the phone, online, via email, through our mobile
        applications or otherwise by using any of our websites or interacting
        with us on social media.
      </p>
      <p>
        If you don't want to read all the detail, here are the things we think
        you'd really want to know:
      </p>
      <ul>
        <li>
          The Sainsbury's Group currently includes Sainsbury's Supermarkets,
          Sainsbury's Bank, Argos, Tu Clothing, Habitat and Nectar.
        </li>
        <li>
          To find out how Nectar or Sainsbury's Bank process your personal data
          follow these links:{" "}
          <a href="https://www.nectar.com/about/privacy-and-legal/privacy-policy/">
            here
          </a>{" "}
          and{" "}
          <a href="https://www.sainsburysbank.co.uk/legals/leg-reg-privacy-policy">
            here
          </a>
        </li>
        <li>
          Your personal data is, where appropriate, shared within the
          Sainsbury's Group.
        </li>
        <li>
          We do use a number of third parties to process your personal data on
          our behalf and some of them are based outside of the United Kingdom.
        </li>
        <li>
          You have a number of rights over your personal data. How you can
          exercise these rights is set out in this notice.
        </li>
        <li>
          We do send direct marketing, if we're allowed to. And we do this to
          encourage you to buy our products and services by sending you offers
          and ideas that we feel will be of benefit to you. If you want us to
          stop then see the{" "}
          <a href="#keeping-you-informed">
            'Keeping you informed about our products and services'
          </a>{" "}
          section below.
        </li>
        <li>
          We do not aim any of our products or services directly at children and
          we do not knowingly collect personal data of children. Some of our
          services and products may be age restricted and this will be shown at
          the point of registration or purchase.
        </li>
        <li>
          We also use your personal data to show you online advertising on
          websites across the Sainsbury's Group, on other websites and online
          media channels. This may be our own marketing, or marketing from
          selected third parties. If you want us to stop using your data for
          this purpose, then see{" "}
          <a href="#using-your-personal-information">'Digital Advertising'</a>{" "}
          below.
        </li>
      </ul>
      {policyContents.map(({ title, id = null, content }, index) => (
        <div key={index} id={id}>
          <AccordionItem title={title}>{content}</AccordionItem>
        </div>
      ))}
    </Card>
  );
};

export const policyContents = [
  {
    title: "Who are we?",
    content: (
      <div>
        <p>
          When we say "we" or "us" in this policy, we are referring to the
          retail companies that form part of the Sainsbury's Group (all with the
          registered office address 33 Charterhouse Street, London, England,
          EC1M 6HA). This privacy policy governs the manner in which Sainsbury's
          Supermarkets Ltd, Argos Limited and Habitat Retail Limited each
          processes personal data collected from our customers, prospective
          customers and/or other individuals who interact with us (together
          "you" or "your"). To find out how Nectar and Sainsbury's Bank process
          your personal data you can read their privacy notices which can be
          found{" "}
          <a href="https://www.nectar.com/about/privacy-and-legal/privacy-policy/">
            here
          </a>{" "}
          and{" "}
          <a href="https://www.sainsburysbank.co.uk/legals/leg-reg-privacy-policy">
            here
          </a>
          .
        </p>
        <p>
          For the purposes of data protection law, the "data controller" (which
          means the entity which determines the purposes and means of any
          processing of your personal data) may be any member of the Sainsbury's
          Group depending on your interactions.
        </p>
      </div>
    ),
  },
  {
    title: "What sorts of personal data do we process?",
    content: (
      <div>
        <style>{`
          .personal-data-table { border-collapse: collapse; }
          .personal-data-table th,
          .personal-data-table td { padding: 8px; }
        `}</style>
        <p>
          Personal data means any information about an individual from which
          that person can be identified. It does not include data where the
          identity has been removed (e.g. anonymous data). We may collect, use,
          store and transfer different kinds of personal data about you which we
          have grouped together as follows:
        </p>
        <table border="1" className="personal-data-table">
          <thead>
            <tr>
              <th>Category of Data</th>
              <th>Examples</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Account data</td>
              <td>
                This is personal data such as your name, home address, date of
                birth, email, telephone number, Nectar card number and payment
                card details.
              </td>
            </tr>
            <tr>
              <td>Transaction data</td>
              <td>
                Information about the goods and services that you buy from us
                (including for example, what they were, when and where you
                bought them and how much you paid).
              </td>
            </tr>
            <tr>
              <td>Analytics data</td>
              <td>
                This is personal data regarding how you interact with us. It
                includes which offers you engage with, how you interact with and
                experience our websites and apps, predictions about your
                interests, shopping habits and characteristics (we call these
                "profiled characteristics").
              </td>
            </tr>
            <tr>
              <td>Interaction data</td>
              <td>
                This is the contents of chats, calls and correspondence with our
                Contact Centre or with chatbots on our websites, and any
                associated data. Details of the emails and other electronic
                communications you receive from us, and how you interact with
                them. For example, whether the communication has been opened, if
                you have clicked on any links within that communication and the
                device you used.
              </td>
            </tr>
            <tr>
              <td>Third party data</td>
              <td>
                This is personal data provided by companies that provide
                customer information or that is publicly available (e.g.
                Experian, CACI, Royal Mail). These might be companies that help
                us verify or update your address, or companies that provide us
                with information on your modelled household profile. This could
                be received at an individual or an anonymous level such as
                information based on your post code.
              </td>
            </tr>
            <tr>
              <td>CCTV data</td>
              <td>
                Information captured by our CCTV, automatic number plate
                recognition (ANPR) and body worn recording devices (together
                "CCTV") if you visit any of our premises. It also includes
                alerts we receive from FRT systems where applicable.
              </td>
            </tr>
            <tr>
              <td>Device data</td>
              <td>
                This is information from devices you use to access our services,
                our websites and apps, received via cookies and other similar
                technologies such as pixels. This could be information such as
                IP addresses, MAC addresses, unique IDs like advertising IDs,
                location data, information about your browser, or details
                contained in URLs used in our communications (e.g. offer links
                in emails).
              </td>
            </tr>
            <tr>
              <td>Preference Data</td>
              <td>
                Information about whether or not you want to receive marketing
                and information about your preferences and opinions that you
                have expressed through surveys, product reviews or within your
                account.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: "Our legal basis for processing your personal data",
    content: (
      <div>
        <p>
          Whenever we process your personal data we need something called a
          "legal basis" for what we do. The different legal bases we rely on
          are:
        </p>
        <ul>
          <li>
            <strong>Consent:</strong> You have told us you are happy for us to
            process your personal information for a specific purpose (s);
          </li>
          <li>
            <strong>Legitimate interests:</strong> The processing is necessary
            for us to conduct our business, but not where our interests are
            overridden by your interests or rights.
          </li>
          <li>
            <strong>Performance of a contract:</strong> We must process your
            personal information in order to be able to provide you with one of
            our products or services;
          </li>
          <li>
            <strong>Vital interests:</strong> The processing of your personal
            information is necessary to protect you or someone else's life;
          </li>
          <li>
            <strong>Legal obligation:</strong> We are required to process your
            personal information by law.
          </li>
        </ul>
        <p>
          In the limited circumstances that we process any{" "}
          <strong>"special category personal data"</strong>, in addition to one
          of the legal bases noted above, we also need to ensure that we have a
          further legal basis for such processing. This will most commonly be
          one of the following:
        </p>
        <ul>
          <li>
            <strong>Explicit Consent</strong>: where you have given us your
            explicit consent to the processing;
          </li>
          <li>
            <strong>Vital Interests:</strong> the processing is necessary to
            protect your vital interests or those of another natural person
            where you are physically or legally incapable of giving consent;
          </li>
          <li>
            <strong>Made public by the data subject:</strong> processing relates
            to personal information that you have made public;
          </li>
          <li>
            <strong>Legal claims and judicial acts</strong>: the processing is
            necessary for the establishment, exercise or defence of legal
            claims; or
          </li>
          <li>
            <strong>Substantial public interest:</strong> the processing is
            necessary for reasons of substantial public interest (e.g.
            regulatory requirements, preventing or detecting unlawful acts).
          </li>
        </ul>
        <p>
          When we refer to <strong>"special category personal data"</strong> we
          mean personal data that reveals racial or ethnic origin, political
          opinions, religious beliefs, philosophical beliefs or trade union
          membership, genetic data, biometric data (where used for
          identification purposes), data concerning health, sex life or sexual
          orientation of an individual.
        </p>
      </div>
    ),
  },
  {
    title:
      "How we use your personal information and our legal basis for doing so",
    id: "using-your-personal-information",
    content: (
      <div>
        <p>We may process your personal data in the following ways:</p>
        <div>
          <table
            border="1"
            style={{ borderCollapse: "collapse", width: "100%" }}
          >
            <thead>
              <tr>
                <th style={{ padding: "8px" }}>
                  <strong>
                    Purpose of processing, legal basis for processing and
                    personal data types processed
                  </strong>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> To provide our products and services
                  <br />
                  We need to use your personal data to make our products and
                  services available to you. This processing may include using
                  your personal data to: deliver products to you; verify your
                  identity and eligibility for products; take payments from you;
                  and ensure you are awarded your Nectar points.
                  <br />
                  <strong>Legal bases:</strong> Performance of a contract;
                  Legitimate interests
                  <br />
                  Performance of a contract - we process your personal data if
                  we have a contract with you and we have to use your data as a
                  necessary part of that contract. Legitimate interest - once
                  you've purchased a product or stopped receiving a service from
                  us, we keep your data for a period of time afterwards as part
                  of our legitimate interests. For example, your information
                  might be needed to deal with a complaint or a product recall.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> To improve your shopping experience
                  <br />
                  We try to understand our customers so we can provide you with
                  a great shopping experience. Understanding how customers use
                  our stores, websites and apps, how they interact with the
                  Sainsbury's Group, where and when they shop and the products
                  and services they buy helps us to do this.
                  <br />
                  <strong>Legal basis:</strong> Legitimate interest
                  <br />
                  This processing helps us to serve you better and to improve
                  our service offerings.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Interaction Data; Third Party Data; Device
                  Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Analytics and profiling
                  <br />
                  We use your personal data for statistical analysis and to help
                  us understand more about our customers. This includes
                  understanding the products and services you buy, how you shop
                  across the whole Sainsbury's Group, creating profiles about
                  you and grouping you together with other Sainsbury's customers
                  who have similar interests or preferences as you. This helps
                  us to serve you better and to find ways to improve our
                  services, stores, apps, and websites. These profiles also help
                  us to send you offers that are more relevant to you and
                  present you with marketing and digital advertising that is
                  more tailored to your interests and preferences and to help
                  our digital marketing platform partners to try to identify
                  other individuals who share similar preferences. This may
                  include using cookies and similar technologies on our website
                  and apps. For more information, please see the Cookie and
                  Digital Advertising policy available on each of our websites.
                  <br />
                  <strong>Legal bases:</strong> Legitimate interest; Consent
                  <br />
                  Legitimate interest: This processing helps us to serve you
                  better and to find ways to improve our services, apps and
                  websites. These profiles help us to send you offers that are
                  more relevant to you. Consent: We gather consent to send
                  marketing. You can change your mind at any time.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Interaction Data; Third Party Data; Device
                  Data, Preference Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Safety, security and fraud
                  prevention
                  <br />
                  We use your personal data to help provide safe and secure
                  environments for our customers to shop in, our colleagues to
                  work in and for our businesses to be conducted. This
                  processing could also include the tracing and recovering
                  debts. To enable this, we monitor behaviour instore and online
                  and carry out checks to help us ensure that our customers are
                  genuine. We do this to prevent fraud and to help customers use
                  our services appropriately.
                  <br />
                  <strong>Legal bases:</strong> Legitimate interests; Legal
                  obligation; Substantial public interest
                  <br />
                  Legitimate interest: To keep our premises and offices secure
                  and to protect our commercial interests and confidential
                  information. Legal obligation: We have certain legal
                  obligations to protect our customers and staff. Substantial
                  public interest: If we process any special category data for
                  these purposes our condition for processing this data is that
                  it's necessary for reasons of substantial public interest,
                  namely the prevention or detection of unlawful acts.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Interaction Data; Third Party Data; Device
                  Data, CCTV Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Contacting you
                  <br />
                  We use your personal data to contact you. This may be in
                  relation to a service update, an issue you have raised with
                  us, to conduct market research, to ask for your feedback or to
                  send you an e-receipt.
                  <br />
                  <strong>Legal bases:</strong> Legitimate interest; Performance
                  of a contract, Consent
                  <br />
                  Performance of a Contract: We may contact you to ensure that
                  we comply with the terms of our contract with you (e.g. if we
                  update any contract terms so as to notify you of what has
                  changed) or to send you your receipt if you shop online.
                  Legitimate interest: We may contact you to conduct activities
                  (e.g. obtain feedback on our performance) to support us in
                  improving our products and/or services and to support the
                  training of our employees. Consent: If you ask us to contact
                  you or send you something (such as an e-receipt instore or to
                  be contacted when a product is back in stock)
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Interaction Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Digital advertising
                  <br />
                  When you browse our websites and apps, or certain other
                  websites, apps, or social media platforms, or when you are
                  using certain services on Connected TVs, you may see
                  advertising that is tailored to you. This may be informed by
                  our use of your personal data as explained in the "Analytics
                  and profiling" section above. These adverts may be presented
                  to you by us, or by selected third parties that we think you
                  might want to hear from. We also check how you and our other
                  shoppers engage with these types of advertisement (for
                  example, if you click on them, or subsequently make a purchase
                  with us), so we can determine whether to show you more or
                  fewer similar advertisements in future. More information about
                  this is set out in our Cookie and Digital Advertising notice
                  and banner. Importantly, we know that not everyone wants to
                  see the kind of advertising described above, so you can always
                  say no when presented with the Cookie and Digital Advertising
                  banner or by managing your "cookie" preferences through our
                  websites and apps by selecting "required only".
                  <br />
                  <strong>Legal basis:</strong> Consent
                  <br />
                  We gather this consent when presenting you with a banner when
                  you visit our websites and apps. You can opt out at any time.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Third Party Data; Device Data, Preference
                  Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Create data models
                  <br />
                  We take personal data and analyse it to look for patterns,
                  trends and characteristics that align with a particular
                  outcome – for example, items that are often purchased
                  together. We use this analysis to create a framework that maps
                  out these patterns, trends and characteristics on an anonymous
                  basis - we call these frameworks "data models" and the process
                  of making them "data modelling".
                  <br />
                  <strong>Legal basis:</strong> Legitimate interest
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Analytics Data; Interaction Data; Third Party Data; Device
                  Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Customer service
                  <br />
                  We record or keep a record of most communications between us.
                  This could be in the form of call recordings, transcripts of
                  calls, emails or web chat conversations. We do this to provide
                  a great service to our customers, to develop our business, to
                  prevent fraud, for colleague training, and if something has
                  gone wrong to manage customer issues.
                  <br />
                  <strong>Legal basis:</strong> Legitimate interest
                  <br />
                  Call recordings, web chat transcripts and other records of
                  communication help us to provide good customer service, train
                  our colleagues and deal with issues that arise.
                  <br />
                  <strong>Data types:</strong> Account Data; Transaction Data;
                  Interaction Data.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>
                  <strong>Purpose:</strong> Resolving legal claims and disputes
                  <br />
                  Occasionally we need to process your personal data to resolve
                  a dispute or a claim and depending on the nature of the
                  dispute it could involve processing any of the personal data
                  types listed above.
                  <br />
                  <strong>Legal basis:</strong> Legitimate interest; Substantial
                  public interest
                  <br />
                  To fairly resolve disputes and claims that arise from time to
                  time. Substantial public interest: If we process any special
                  category data for this purpose our condition for processing
                  this data is that it's necessary for reasons of substantial
                  public interest, namely the prevention or detection of
                  unlawful acts.
                  <br />
                  <strong>Data types:</strong> All data types.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    title: "Keeping you informed about our products and services",
    id: "keeping-you-informed",
    content: (
      <div>
        <p>
          We would like to tell you about great offers, ideas, products and
          services from the Sainsbury's Group and our suppliers that we think
          you might be interested in. Where we have consent or it is in our
          legitimate interests to do so, we may do this through the post, by
          email, text message or phone.
        </p>
        <p>
          We won't send you marketing messages if you tell us not to, but if you
          receive a service from us we will still need to send you occasional
          service-related messages and may still send you emails requesting
          feedback or surveys (you can always opt out of these via the survey
          email itself). If you wish to amend your marketing preferences, you
          can do so by logging into any of your Sainsbury's Group accounts and
          following the directions, or by logging into our{" "}
          <a
            rel="noopener noreferrer"
            target="_blank"
            href="https://permissions.sainsburys.co.uk/"
          >
            Customer Preference Centre
          </a>
          .
        </p>
        <p>
          Please note that it can take a little while for all marketing to stop
          once you either withdraw your consent or tell us you'd like to opt out
          of marketing. This is because some marketing may have been identified
          as relevant to your interests and may already be in transit, it cannot
          therefore be immediately stopped.
        </p>
        <p>
          If you don't want to receive coupons at till in Sainsbury's based on
          your shopping then let us know by emailing{" "}
          <a href="mailto:privacy@sainsburys.co.uk">privacy@sainsburys.co.uk</a>
          . You might still receive coupons but they won't be based on
          information we have about you.
        </p>
      </div>
    ),
  },
  {
    title: "CCTV",
    content: (
      <div>
        <p>
          We use CCTV across all sites in the Sainsbury's Group for the
          protection of our colleagues, customers and business. This includes
          investigating accidents, incidents, criminal activities, and breaches
          of our policies. CCTV is also in operation in our petrol stations and
          car parks for these purposes. Some car parks are run by third parties,
          so please check the local notice. We also use CCTV for the purpose of
          operational efficiency, such as monitoring the availability of
          products on our shelves.
        </p>
        <p>
          Some of our colleagues also wear body worn devices to protect
          themselves and our customers. These are only activated in high-risk
          situations such as when there is a threat of violence. Body worn
          devices record both audio and video.
        </p>
        <p>
          Occasionally we are asked to share CCTV with the Police or other
          public or regulatory authorities or individuals or businesses seeking
          to protect their rights or the rights of others. We only share CCTV if
          we consider a request to be appropriate and lawful pursuant to the
          requirements of data protection legislation.
        </p>
      </div>
    ),
  },
  {
    title: "Facial Recognition Technology",
    id: "facial-recognition-technology",
    content: (
      <div>
        <p>
          We have implemented FRT in a number of stores for the purposes of
          protecting our colleagues from harm and aggression, to provide a
          secure shopping environment for our customers and to prevent and deter
          criminal activity within our stores. FRT in our stores is provided by
          Facewatch, you can access their privacy policy{" "}
          <a href="https://www.facewatch.co.uk/privacy/">here</a>. As Facewatch
          is directly collecting and using the images captured by the
          technology, they act as independent controllers and are the data
          controller responsible for the processing of personal data by way of
          facial recognition. We supply details (including CCTV images) of
          people responsible for unlawful acts within our premises to Facewatch
          which they, following further checks, may add to a list of subjects of
          interest. We do not have access to that list of subjects of interest
          but receive alerts when they enter our stores. Other retailers who use
          Facewatch FRT also supply details which may be added by Facewatch to
          the list of subjects of interest. We display clear signage in all
          stores where facial recognition technology is used.
        </p>
        <p>
          If you have any questions or concerns about our use of this
          technology, please do not hesitate to contact us by emailing{" "}
          <a href="mailto:privacy@sainsburys.co.uk">privacy@sainsburys.co.uk</a>
          . If you would like to get in touch with Facewatch directly you can
          contact them at{" "}
          <a href="mailto:dpo@facewatch.co.uk">dpo@facewatch.co.uk</a>
        </p>
      </div>
    ),
  },
  {
    title: "Who might we share your personal information with?",
    content: (
      <div>
        <p>
          We may share your personal information with the following third
          parties as part of the purposes set out in "How do we use your
          personal information?" above:
        </p>
        <p>
          <strong>The Sainsbury's Group</strong> - we may share your personal
          information with the other companies within the Sainsbury's Group so
          that we can provide you with a high quality, personalised and tailored
          service (including relevant marketing) across the Sainsbury's Group
          and for the purposes that are set out in this privacy policy.
        </p>
        <p>
          <strong>Companies we partner with</strong> - we work with different
          companies so that we can provide products and services you require
          from us or we think you might be interested in. These third parties
          include:
        </p>
        <ul>
          <li>
            Companies that provide insights and analytics services so we can
            stock the right products, send the relevant marketing campaigns and
            understand our business and customers better;
          </li>
          <li>
            Advertising companies, partners and suppliers, or digital media
            platform partners like Meta, Tiktok, X and Google, who help us
            target Sainsbury's Group or selected third party partner adverts
            online and on other media;
          </li>
          <li>
            Suppliers that deliver products directly to you or that need to
            conduct a product recall;
          </li>
          <li>
            Market research partners, who help us to collect and analyse
            customer behaviour;
          </li>
          <li>
            Companies that deploy our marketing campaigns because they need some
            information to carry out these services;
          </li>
          <li>
            NewDay, the company who provide Argos Pay, so they can understand
            how you use their product and provide more relevant marketing where
            you have consented. The Argos Pay privacy policy can be found{" "}
            <a href="https://urldefense.com/v3/__https:/portal.newdaycards.com/argospay/privacy-policy__;!!BQ_wX6TuWITc6hG5HA!UMsfNJIkw75fXnmd3sPYhT5dnhV6-f9Hwq_L3llw6o69eX-eet2Zyhy0qNiohOGNJOVd-tNdVDHir1J5y3CjIe6UdRIyf95hcwrctDm1$">
              here
            </a>
          </li>
          <li>
            Scheme providers and companies who provide payment solutions who
            process your information as controllers and processors – such as
            Visa, Mastercard and Checkout –in order to manage your account and
            for your payments to be processed;
          </li>
          <li>
            Our agents, advisers or others involved in running accounts and
            services for you and your business or collecting what you or your
            business owe Group companies.
          </li>
          <li>
            Third party vendors who help us manage and maintain the Sainsbury's
            Group IT infrastructure and provide IT security services;
          </li>
          <li>
            Companies who deliver products to you that order on our websites;
          </li>
          <li>
            Where relevant, our professional advisors, such as lawyers and
            consultants;
          </li>
          <li>
            Security and fraud prevention companies to ensure the safety and
            security of our customers, colleagues and business;
          </li>
          <li>
            Companies which run our contact centres because they need your
            personal information to identify and contact you;
          </li>
          <li>
            Companies who assess faults and repair products on our behalf;
          </li>
          <li>
            Companies who administer competitions for us so they run smoothly;
          </li>
          <li>
            Companies that enable us to collect your reviews and comments, both
            online and offline; and
          </li>
          <li>Companies that help us with community and social goals.</li>
        </ul>
        <p>
          If you use the services provided by another company to interact with
          us, such as a virtual assistant or a social media platform, please be
          aware that your data is also subject to the privacy policies of these
          companies.
        </p>
        <p>
          Additionally the WiFi that is available in our stores and offices is
          provided by a third party. If you decide to use this service then your
          data is subject to their privacy policy, details of which are provided
          when you sign up.
        </p>
        <p>
          <strong>Other organisations and individuals</strong> - we may share
          your personal information in certain scenarios. For example:
        </p>
        <ul>
          <li>
            If we're discussing selling or transferring part or all of a
            Sainsbury's Group business, we may share information about you to
            prospective purchasers and their advisers - but only so they can
            evaluate the relevant business; or
          </li>
          <li>
            If we are reorganised or sold to another organisation, we may
            transfer information we hold about you to them so they can continue
            to provide the Services to you.
          </li>
          <li>
            If we are required to by law, under any code of practice by which we
            are bound or where we are asked to do so by a public or regulatory
            authority;
          </li>
          <li>
            If we need to do so in order to exercise or protect our legal
            rights, users, systems and services; or
          </li>
          <li>
            In response to requests from individuals (or their representatives)
            seeking to protect their rights or the rights of others. We will
            only share your personal information in response to requests which
            do not override your privacy interests. For example, we will not
            share your personal information with individuals who are merely
            curious about you, but we will share your personal information to
            e.g. insurers, solicitors, employers etc. which have a legitimate
            interest in your personal information.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "International transfers of personal information",
    content: (
      <div>
        <p>
          From time to time we transfer your personal information to our
          Sainsbury's Group companies, suppliers or service providers based
          outside of the United Kingdom for the purposes described in this
          privacy policy (please see the "Who might we share your personal
          information with?" section above for further details). When we do
          this, your personal information will continue to be subject to one or
          more appropriate safeguards set out in the law. These might be the use
          of model contracts in a form approved by regulators, or having our
          suppliers sign up to an independent privacy scheme approved by
          regulators.
        </p>
      </div>
    ),
  },
  {
    title: "Your rights",
    content: (
      <div>
        <p>
          You have a number of rights under data protection legislation which,
          in certain circumstances, you may be able to exercise in relation to
          the personal information we process about you. These include:
        </p>
        <ul>
          <li>
            the right to access a copy of the personal information we hold about
            you;
          </li>
          <li>
            the right to correction of inaccurate personal information we hold
            about you;
          </li>
          <li>the right to restrict our use of your personal information;</li>
          <li>the right to be forgotten;</li>
          <li>the right of data portability; and</li>
          <li>the right to object to our use of your personal information.</li>
        </ul>
        <p>
          If you are seeking to exercise any of these rights, please contact us
          using the details in the "Contact Us" section below. Please note that
          we will need to verify your identity before we can fulfil any of your
          rights under data protection law. This helps us to protect the
          personal information belonging to our customers against fraudulent
          requests.
        </p>
      </div>
    ),
  },
  {
    title: "Automated decision making and profiling",
    content: (
      <div>
        <p>
          We use automated decision making, including profiling, in certain
          circumstances, such as when it is in our legitimate interests to do
          so, or where we have a right to do so because it is necessary for us
          to enter into, and perform, a contract with you. We use profiling to
          enable us to give you the best service across the Sainsbury's Group,
          including specific marketing which we believe you will be interested
          in.
        </p>
        <p>
          You have the right not to be subject to a decision based solely on
          automated processing, including profiling, which has legal effects for
          you or affects you in another significant way, unless appropriate
          safeguards are in place. We will put those safeguards in place which
          means it will be clear if these decisions are taking place, you will
          have a right to contest them and to have a human review the decision.
        </p>
        <p>
          If you are seeking to exercise this right, please contact us using the
          details in the "Contact Us" section below.
        </p>
      </div>
    ),
  },
  {
    title: "How long will we keep your personal information for?",
    content: (
      <div>
        <p>
          We will keep your personal information for the purposes set out in
          this privacy policy and in accordance with the law and only for as
          long as is necessary. In most cases, our retention period will come to
          an end 7 years after the end of your relationship with us.
        </p>
      </div>
    ),
  },
  {
    title: "Security",
    content: (
      <div>
        <p>
          We take protecting your personal information seriously and are
          continuously developing our security systems and processes. Some of
          the controls we have in place are:
        </p>
        <ul>
          <li>
            We limit physical access to our buildings and user access to our
            systems to only those that we believe are entitled to be there;
          </li>
          <li>
            We use technology controls for our information systems, such as
            firewalls, user verification, strong data encryption, and separation
            of roles, systems & data;
          </li>
          <li>
            Systems are proactively monitored through a "detect and respond"
            information security function;
          </li>
          <li>
            We utilize industry "good practice" standards to support the
            maintenance of a robust information security management system; and
          </li>
          <li>
            We enforce a "need to know" policy, for access to any data or
            systems.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Contact us",
    content: (
      <div>
        <p>
          If you would like to exercise one of your rights as set out in the
          "Your rights" or "Automated decision making and profiling" sections
          above, or you have a question or a complaint about this policy, or the
          way your personal information is processed, please contact us by one
          of the following means:
        </p>
        <div>
          <div>
            By email:
            <a href="mailto:privacy@sainsburys.co.uk">
              privacy@sainsburys.co.uk
            </a>
          </div>
          <div>
            By post: Data Protection Officer at Privacy Team, Sainsbury's
            Supermarkets Ltd, 17th Floor, Arndale House, Manchester, M4 3AL
          </div>
          <p>
            You also have the right to lodge a complaint with the UK regulator,
            the Information Commissioner. Please visit ico.org.uk/concerns to
            find out more.
          </p>
        </div>
      </div>
    ),
  },
  {
    title: "Policy change",
    content: (
      <div>
        <p>This privacy policy was most recently updated in April 2026.</p>
      </div>
    ),
  },
];



// WEBPACK FOOTER //
// ./src/components/pages/PrivacyPolicy.js